/**
 * 閲覧済みイベントトラッカー
 *
 * Intersection Observer を使用して、ビューポートに表示されたイベントカードの
 * event_id を収集し、ページ離脱時に POST /api/viewed_events へ一括送信する。
 *
 * イベントカード要素に data-event-id 属性が必要。
 *
 * パフォーマンス最適化:
 *   MutationObserver の監視範囲をイベント一覧コンテナに限定し、
 *   document.body 全体の subtree 監視による負荷を回避する。
 */

const ViewedEventsTracker = (() => {
  const viewedIds = new Set();
  const sentIds = new Set();
  let observer = null;
  let isInitialized = false;

  // イベント一覧が描画されるコンテナのセレクタ一覧
  // MutationObserver はこれらのコンテナ内のみを監視する
  const EVENT_LIST_CONTAINER_SELECTORS = [
    '#search-result-wrapper', // 検索結果ページ
    '.js-scroll-content',     // トップページ横スクロールイベント一覧
    '.timeline-box',          // トップページ人気イベント一覧
  ];

  // ログイン状態を確認
  const isUserSignedIn = () => {
    return document.body.dataset.signedIn === 'true';
  };

  // 未送信の閲覧済みイベントIDをAPIに送信
  const flush = () => {
    if (viewedIds.size === 0) return;
    if (!isUserSignedIn()) return;

    const ids = [...viewedIds].map(Number);

    // navigator.sendBeacon を使用（ページ離脱時でも確実に送信される）
    const blob = new Blob(
      [JSON.stringify({ event_ids: ids })],
      { type: 'application/json' }
    );

    try {
      navigator.sendBeacon('/api/viewed_events', blob);
    } catch (e) {
      // sendBeacon が失敗した場合は無視（次回送信で再試行される）
    }

    ids.forEach(id => sentIds.add(id));
    viewedIds.clear();
  };

  // IntersectionObserver のコールバック
  const onIntersect = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const eventId = entry.target.dataset.eventId;
        if (eventId && !sentIds.has(Number(eventId))) {
          viewedIds.add(eventId);
        }
      }
    });
  };

  // 指定ノード内の data-event-id 要素を IntersectionObserver に登録
  const observeEventElements = (root) => {
    if (!observer) return;

    // root 自体が data-event-id を持つ場合
    if (root.dataset && root.dataset.eventId) {
      observer.observe(root);
    }

    // root の子孫を探索
    if (root.querySelectorAll) {
      const children = root.querySelectorAll('[data-event-id]');
      children.forEach((child) => {
        observer.observe(child);
      });
    }
  };

  // 既存の要素を監視対象に追加
  const observeExisting = () => {
    if (!observer) return;
    const elements = document.querySelectorAll('[data-event-id]');
    elements.forEach((el) => {
      observer.observe(el);
    });
  };

  // ---- MutationObserver 関連 ----

  // 監視中のコンテナを追跡（重複監視を防止）
  const observedContainers = new WeakSet();

  // コンテナ内の変更を監視する MutationObserver を作成
  const createContainerObserver = () => {
    return new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          observeEventElements(node);
        });
      });
    });
  };

  // 特定のコンテナ要素に MutationObserver をアタッチ
  const attachToContainer = (container) => {
    if (observedContainers.has(container)) return;
    observedContainers.add(container);

    // コンテナ内の既存要素を監視
    observeEventElements(container);

    // コンテナ内の動的追加を監視
    const containerObserver = createContainerObserver();
    containerObserver.observe(container, {
      childList: true,
      subtree: true,
    });
  };

  // 既存のコンテナを探してアタッチ
  const attachToExistingContainers = () => {
    EVENT_LIST_CONTAINER_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((container) => {
        attachToContainer(container);
      });
    });
  };

  // コンテナ自体が動的に追加される場合に備えた軽量ルートオブザーバー
  //
  // body に subtree: true を付けるが、コールバック内では高コストな探索を避ける:
  //   - node.matches() でコンテナ判定（O(1)）
  //   - node.dataset.eventId の直接参照（O(1)）
  //   - querySelectorAll は子要素数が少ないノードに限定
  const LARGE_NODE_THRESHOLD = 100;

  const watchForContainers = () => {
    const selectorSet = EVENT_LIST_CONTAINER_SELECTORS.join(', ');

    const rootObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;

          // 追加されたノード自体がコンテナか確認（O(1)）
          if (node.matches && node.matches(selectorSet)) {
            attachToContainer(node);
            continue;
          }

          // 追加されたノード自体が data-event-id を持つ場合（コンテナ外のカード、O(1)）
          if (node.dataset && node.dataset.eventId) {
            observer.observe(node);
            continue;
          }

          // 大きいサブツリーの深い探索を抑制
          if (!node.querySelectorAll || node.childElementCount > LARGE_NODE_THRESHOLD) continue;

          // 小さいサブツリー内にコンテナがあるか確認
          node.querySelectorAll(selectorSet).forEach((container) => {
            attachToContainer(container);
          });
        }
      }
    });

    rootObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  // 初期化
  const init = () => {
    if (isInitialized) return;
    if (typeof IntersectionObserver === 'undefined') return;

    isInitialized = true;

    observer = new IntersectionObserver(onIntersect, {
      threshold: 0.5, // 50%以上表示されたら「見た」と判定
    });

    // 既存要素を監視
    observeExisting();

    // 既存コンテナにアタッチ
    attachToExistingContainers();

    // コンテナの動的追加を監視
    watchForContainers();

    // ページ離脱時に送信
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flush();
      }
    });

    // ページアンロード時にも送信（保険）
    window.addEventListener('beforeunload', () => {
      flush();
    });
  };

  // DOMContentLoaded で初期化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return {
    init,
    flush,
    observeExisting,
  };
})();

export default ViewedEventsTracker;
